export interface TtsPronunciationEntry {
    source: string,
    replacement: string,
}

const isJsonObject = (value: unknown): value is Record<string, unknown> => {
    return value != null && typeof value === 'object' && !Array.isArray(value);
};

const skipWhitespace = (value: string, index: number): number => {
    let nextIndex = index;

    while (nextIndex < value.length && /\s/.test(value[nextIndex])) {
        nextIndex += 1;
    }

    return nextIndex;
};

const parseQuotedString = (value: string, index: number): { result: string, nextIndex: number } | null => {
    const quote = value[index];

    if (quote !== '"' && quote !== "'") {
        return null;
    }

    let result = '';
    let nextIndex = index + 1;

    while (nextIndex < value.length) {
        const current = value[nextIndex];

        if (current === quote) {
            return { result, nextIndex: nextIndex + 1 };
        }

        if (current === '\\') {
            const escaped = value[nextIndex + 1];

            if (escaped == null) {
                return null;
            }

            switch (escaped) {
                case 'b':
                    result += '\b';
                    nextIndex += 2;
                    break;
                case 'f':
                    result += '\f';
                    nextIndex += 2;
                    break;
                case 'n':
                    result += '\n';
                    nextIndex += 2;
                    break;
                case 'r':
                    result += '\r';
                    nextIndex += 2;
                    break;
                case 't':
                    result += '\t';
                    nextIndex += 2;
                    break;
                case 'u': {
                    const hexValue = value.slice(nextIndex + 2, nextIndex + 6);

                    if (!/^[0-9a-fA-F]{4}$/.test(hexValue)) {
                        return null;
                    }

                    result += String.fromCharCode(parseInt(hexValue, 16));
                    nextIndex += 6;
                    break;
                }
                default:
                    result += escaped;
                    nextIndex += 2;
                    break;
            }

            continue;
        }

        result += current;
        nextIndex += 1;
    }

    return null;
};

const parseRelaxedTtsPronunciationsMap = (value: string): Record<string, string> => {
    let index = skipWhitespace(value, 0);
    const result = {} as Record<string, string>;

    if (value[index] !== '{') {
        throw new Error('Expected object start.');
    }

    index = skipWhitespace(value, index + 1);

    if (value[index] === '}') {
        index = skipWhitespace(value, index + 1);

        if (index !== value.length) {
            throw new Error('Unexpected trailing content.');
        }

        return result;
    }

    while (index < value.length) {
        const key = parseQuotedString(value, index);

        if (!key) {
            throw new Error('Expected quoted key.');
        }

        index = skipWhitespace(value, key.nextIndex);

        if (value[index] !== ':') {
            throw new Error('Expected colon.');
        }

        index = skipWhitespace(value, index + 1);

        const replacement = parseQuotedString(value, index);

        if (!replacement) {
            throw new Error('Expected quoted value.');
        }

        result[key.result] = replacement.result;
        index = skipWhitespace(value, replacement.nextIndex);

        if (value[index] === ',') {
            index = skipWhitespace(value, index + 1);

            if (value[index] === '}') {
                throw new Error('Trailing comma.');
            }

            continue;
        }

        if (value[index] === '}') {
            index = skipWhitespace(value, index + 1);

            if (index !== value.length) {
                throw new Error('Unexpected trailing content.');
            }

            return result;
        }

        throw new Error('Expected comma or object end.');
    }

    throw new Error('Expected object end.');
};

const parseTtsPronunciationsMapValue = (value: string): unknown => {
    try {
        return JSON.parse(value);
    } catch {
        return parseRelaxedTtsPronunciationsMap(value);
    }
};

export const trimTtsPronunciationsMapJson = (value?: string): string => {
    return (value ?? '').trim();
};

export const getNormalizedPronunciationKey = (value: string): string => {
    return value.trim().toLowerCase();
};

const normalizePronunciationReplacement = (value?: string): string => {
    const replacement = value ?? '';

    return !!replacement.trim().length ? replacement.trim() : replacement;
};

export const sanitizeTtsPronunciationEntries = (entries?: TtsPronunciationEntry[]): TtsPronunciationEntry[] => {
    if (!entries?.length) {
        return [];
    }

    return entries
        .map((entry) => ({
            source: entry?.source?.trim() ?? '',
            replacement: normalizePronunciationReplacement(entry?.replacement),
        }))
        .filter((entry) => !!entry.source.length && !!entry.replacement.length);
};

export const buildTtsPronunciationsMap = (entries?: TtsPronunciationEntry[]): Record<string, string> => {
    const nextMap = {} as Record<string, string>;

    sanitizeTtsPronunciationEntries(entries).forEach((entry) => {
        nextMap[entry.source] = entry.replacement;
    });

    return nextMap;
};

export const serializeTtsPronunciationsMap = (entries?: TtsPronunciationEntry[]): string => {
    const nextMap = buildTtsPronunciationsMap(entries);

    return Object.keys(nextMap).length ? JSON.stringify(nextMap) : '';
};

export const parseTtsPronunciationsMap = (value?: string): Record<string, string> | null => {
    const trimmedValue = trimTtsPronunciationsMapJson(value);

    if (!trimmedValue.length) {
        return null;
    }

    const parsedValue = parseTtsPronunciationsMapValue(trimmedValue);

    if (!isJsonObject(parsedValue)) {
        return null;
    }

    return parsedValue as Record<string, string>;
};

export const getTtsPronunciationEntries = (value?: string): TtsPronunciationEntry[] => {
    const parsedValue = parseTtsPronunciationsMap(value);

    if (!parsedValue) {
        return [];
    }

    return Object.entries(parsedValue).map(([source, replacement]) => ({
        source,
        replacement: typeof replacement === 'string' ? replacement : '',
    }));
};

export const normalizeTtsPronunciationsMap = (value?: string): string => {
    const trimmedValue = trimTtsPronunciationsMapJson(value);

    if (!trimmedValue.length) {
        return '';
    }

    try {
        const validationError = getTtsPronunciationsMapValidationError(trimmedValue);

        if (!!validationError.length) {
            return trimmedValue;
        }

        return serializeTtsPronunciationsMap(getTtsPronunciationEntries(trimmedValue));
    } catch {
        return trimmedValue;
    }
};

export const formatTtsPronunciationsMap = (value?: string): string => {
    const trimmedValue = trimTtsPronunciationsMapJson(value);

    if (!trimmedValue.length) {
        return '';
    }

    try {
        const validationError = getTtsPronunciationsMapValidationError(trimmedValue);

        if (!!validationError.length) {
            return trimmedValue;
        }

        const parsedValue = parseTtsPronunciationsMap(trimmedValue);

        if (!parsedValue) {
            return '';
        }

        return JSON.stringify(parsedValue, null, 2);
    } catch {
        return trimmedValue;
    }
};

export const getTtsPronunciationsMapValidationError = (value?: string): string => {
    const trimmedValue = trimTtsPronunciationsMapJson(value);

    if (!trimmedValue.length) {
        return '';
    }

    try {
        const parsedValue = parseTtsPronunciationsMapValue(trimmedValue);

        if (!isJsonObject(parsedValue)) {
            return 'TTS pronunciations map must be a JSON object.';
        }

        const seenKeys = new Set<string>();

        for (const [source, replacement] of Object.entries(parsedValue)) {
            const trimmedSource = source.trim();

            if (!trimmedSource.length) {
                return 'TTS pronunciations map keys cannot be empty.';
            }

            if (typeof replacement !== 'string') {
                return 'TTS pronunciations map values must be strings.';
            }

            if (!replacement.length) {
                return 'Each pronunciation replacement must be non-empty.';
            }

            const normalizedKey = getNormalizedPronunciationKey(trimmedSource);

            if (seenKeys.has(normalizedKey)) {
                return 'Pronunciation sources must be unique, ignoring case.';
            }

            seenKeys.add(normalizedKey);
        }

        return '';
    } catch {
        return 'TTS pronunciations map must be valid JSON.';
    }
};
